import { createNamespacedHelpers } from "vuex";
import { FRUITS, WALL_THICKNESS } from '@utils/game/circleMerge/constants.js';
import { audioManager } from '@utils/game/circleMerge/audioManager.js';
import Matter from 'matter-js';
import { aesEncrypt, getRandomElement, isOpenMobile } from '@utils/index.js';
import { gameAdv } from "@utils/gameAdv.js";
import vailcode from "@utils/errcode";
const walletVuex = createNamespacedHelpers("wallet");
export default {
  name: "circleMergePage",
  computed: {
    ...walletVuex.mapState(["isMobile", "theme", 'setting']),
    startBtnText() {
      return this.lastScore > 0 ? 'Start Game (Ad)' : this.$lang('Start Game');
    }
  },
  data() {
    return {
      revives: 10,
      maxRevives: 10,
      FRUITS,
      WALL_THICKNESS,
      GAME_WIDTH: 0,
      GAME_HEIGHT: 0,
      audio: audioManager,
      lastScore: 0,
      score: 0,
      timeVal: null,
      stimeVal: null,
      leftTime: 0,
      prizePool: 0,
      timePeriod: "",
      userReward: null,
      popUserRewardShow: false,
      popGameRankShow: false,
      popStartShow: false,
      popLostShow: false,
      popOpenMobileShow: false,
      nextFruitIndex: 0,
      gameOver: false,
      guideX: 0,
      isMuted: false,
      engine: null,
      runner: null,
      render: null,
      mergeGroups: new Map()
    };
  },
  watch: {
    "gameOver": {
      handler(newVal, oldVal) {
        if (newVal) {
          this.popLostShow = true;
          this.end();
        } else {}
      }
    },
    score: {
      handler() {
        clearTimeout(this.timeVal);
        this.timeVal = setTimeout(() => {
          this.end();
        }, 200);
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    try {
      clearTimeout(this.stimeVal);
      this.end();
    } catch (error) {}
    this.$nextTick(() => {
      next();
    });
  },
  methods: {
    createFruit(x, y, index, isStatic = false) {
      const fruitIndex = Math.min(index, this.FRUITS.length - 1);
      const fruit = this.FRUITS[fruitIndex];
      const body = Matter.Bodies.circle(x, y, fruit.radius, {
        label: `fruit_${fruitIndex}`,
        restitution: 0.3,
        friction: 0.2,
        frictionAir: 0.01,
        isStatic: isStatic,
        render: {
          fillStyle: fruit.color,
          strokeStyle: fruit.color,
          lineWidth: 2
        }
      });
      body.spawnTime = Date.now();
      return body;
    },
    async startGame() {
      if (!this.$refs.scene) return;
      await this.start();
      if (this.lastScore > 0) {
        let advData = getRandomElement(this.advList);
        if (advData) {
          gameAdv.show(advData, this.timePeriod, res => {
            this.initGame();
          }, this);
        } else {
          this.initGame();
        }
      } else {
        this.initGame();
      }
    },
    async initGame() {
      if (!this.$refs.scene) return;
      this.popStartShow = false;
      this.popLostShow = false;
      this.revives = this.maxRevives;
      this.audio.init();
      if (this.runner) Matter.Runner.stop(this.runner);
      if (this.render) {
        Matter.Render.stop(this.render);
        this.render.canvas.remove();
      }
      if (this.engine) {
        Matter.World.clear(this.engine.world, false);
        Matter.Engine.clear(this.engine);
      }
      this.$refs.scene.innerHTML = '';
      this.mergeGroups = new Map();
      const engine = Matter.Engine.create({
        enableSleeping: false,
        gravity: {
          x: 0,
          y: 1.2,
          scale: 0.001
        }
      });
      this.engine = engine;
      const render = Matter.Render.create({
        element: this.$refs.scene,
        engine: engine,
        options: {
          width: this.GAME_WIDTH,
          height: this.GAME_HEIGHT,
          wireframes: false,
          background: 'transparent',
          pixelRatio: window.devicePixelRatio
        }
      });
      this.render = render;
      const ground = Matter.Bodies.rectangle(this.GAME_WIDTH / 2, this.GAME_HEIGHT + this.WALL_THICKNESS / 2, this.GAME_WIDTH, this.WALL_THICKNESS, {
        isStatic: true,
        label: 'wall',
        friction: 0.5
      });
      const leftWall = Matter.Bodies.rectangle(-this.WALL_THICKNESS / 2, this.GAME_HEIGHT / 2, this.WALL_THICKNESS, this.GAME_HEIGHT * 2, {
        isStatic: true,
        label: 'wall',
        friction: 0.5
      });
      const rightWall = Matter.Bodies.rectangle(this.GAME_WIDTH + this.WALL_THICKNESS / 2, this.GAME_HEIGHT / 2, this.WALL_THICKNESS, this.GAME_HEIGHT * 2, {
        isStatic: true,
        label: 'wall',
        friction: 0.5
      });
      Matter.Composite.add(engine.world, [ground, leftWall, rightWall]);
      Matter.Events.on(engine, 'collisionStart', event => {
        for (const pair of event.pairs) {
          const {
            bodyA,
            bodyB
          } = pair;
          if (bodyA.label?.startsWith('fruit_') && bodyA.label === bodyB.label) {
            const label = bodyA.label;
            if (!this.mergeGroups.has(label)) this.mergeGroups.set(label, new Set());
            this.mergeGroups.get(label).add(bodyA);
            this.mergeGroups.get(label).add(bodyB);
          }
        }
      });
      Matter.Events.on(engine, 'beforeUpdate', () => {
        if (this.mergeGroups.size > 0) {
          this.mergeGroups.forEach((bodiesSet, label) => {
            const bodies = Array.from(bodiesSet).filter(b => Matter.Composite.allBodies(engine.world).includes(b));
            if (bodies.length < 2) {
              bodiesSet.clear();
              return;
            }
            const count = bodies.length >= 3 ? 3 : 2;
            const toMerge = bodies.slice(0, count);
            const index = parseInt(label.split('_')[1]);
            let sumX = 0,
              sumY = 0;
            toMerge.forEach(b => {
              sumX += b.position.x;
              sumY += b.position.y;
            });
            const avgX = sumX / count;
            const avgY = sumY / count;
            Matter.Composite.remove(engine.world, toMerge);
            const levelSkip = count === 3 ? 2 : 1;
            const nextIndex = Math.min(index + levelSkip, this.FRUITS.length - 1);
            if (index < this.FRUITS.length - 1) {
              const newFruit = this.createFruit(avgX, avgY, nextIndex, false);
              Matter.Composite.add(engine.world, newFruit);
              this.audio.playSound(count === 3 ? 'triple' : 'merge', index);
              this.score = this.score + (count === 3 ? this.FRUITS[index].score * 5 : this.FRUITS[index].score * 2);
            }
            toMerge.forEach(b => bodiesSet.delete(b));
          });
        }
      });
      Matter.Events.on(engine, 'afterUpdate', () => {
        const bodies = Matter.Composite.allBodies(engine.world);
        const now = Date.now();
        for (let body of bodies) {
          if (body.label?.startsWith('fruit_') && body.position.y < 90 && Math.abs(body.velocity.y) < 0.1 && now - body.spawnTime > 3000) {
            this.gameOver = true;
            //this.audio.playSound('gameover');
          }
        }
      });

      const runner = Matter.Runner.create();
      this.runner = runner;
      Matter.Runner.run(runner, engine);
      Matter.Render.run(render);
      this.score = 0;
      this.gameOver = false;
      this.generateNextFruit();
    },
    generateNextFruit() {
      const maxStartIdx = 3;
      this.nextFruitIndex = Math.floor(Math.random() * (maxStartIdx + 1));
    },
    // ---------------------------
    // 控制输入
    // ---------------------------
    handlePointerMove(e) {
      if (this.gameOver) return;
      const rect = this.$refs.scene.getBoundingClientRect();
      const x = Math.max(this.FRUITS[this.nextFruitIndex].radius + 10, Math.min(this.GAME_WIDTH - this.FRUITS[this.nextFruitIndex].radius - 10, e.clientX - rect.left));
      this.guideX = x;
    },
    handlePointerDown(e) {
      if (this.gameOver) return;
      const now = Date.now();
      if (now - this.lastSpawnTime < 500) return;
      const rect = this.$refs.scene.getBoundingClientRect();
      const x = Math.max(this.FRUITS[this.nextFruitIndex].radius + 10, Math.min(this.GAME_WIDTH - this.FRUITS[this.nextFruitIndex].radius - 10, e.clientX - rect.left));
      const fruit = this.createFruit(x, 50, this.nextFruitIndex);
      Matter.Composite.add(this.engine.world, fruit);
      this.audio.playSound("drop");
      this.lastSpawnTime = now;
      this.generateNextFruit();
    },
    async start() {
      let rs = await this.$http.post("/game/start", {
        game_type: "circleMerge"
      });
      vailcode(rs, () => {
        this.prizePool = rs.data.keys ?? 0;
        this.timePeriod = rs.data.time_period ?? "";
        this.leftTime = rs.data.left_time ?? 0;
        this.lastScore = rs.data.last_score ?? 0;
      });
    },
    async end() {
      if (this.lastScore >= this.score) return;
      let gameData = aesEncrypt(JSON.stringify({
        game_type: "circleMerge",
        level: 1,
        score: this.score,
        time_period: this.timePeriod,
        time: new Date().getTime()
      }));
      let rs = await this.$http.post("/game/score", {
        gameData: gameData,
        '_n': 1
      });
      if (rs.code == 0) {
        this.lastScore = this.score;
        // this.level = rs.data.level ?? 1;
        // this.score = rs.data.score ?? 0;
        this.prizePool = rs.data.keys ?? 0;
      }
    },
    async init() {
      let rs = await this.$http.post("/game/init", {
        game_type: "circleMerge"
      });
      vailcode(rs, () => {
        this.leftTime = rs.data.left_time ?? 0;
        this.prizePool = rs.data.keys ?? 0;
        this.advList = rs.data.advList ?? [];
        this.timePeriod = rs.data.time_period ?? "";
        this.userReward = rs.data.userReward ?? null;
        this.lastScore = rs.data.score ?? 0;
        if (this.userReward?.total_keys > 0 || this.userReward?.total_taps > 0) {
          setTimeout(() => {
            this.popUserRewardShow = true;
          }, 500);
        }
        this.countDown();
      });
    },
    handleCancel() {
      this.initGame();
      this.popLostShow = false;
    },
    handleConfrim() {
      this.revives > 0 ? this.handlerRevive() : this.handleCancel();
    },
    handlerRevive() {
      if (!this.engine || this.revives <= 0) return;
      const callback = async res => {
        this.popLostShow = false;
        const bodies = Matter.Composite.allBodies(this.engine.world);
        const fruitBodies = bodies.filter(b => b.label?.startsWith('fruit_'));
        if (fruitBodies.length > 0) {
          // Find the fruit with the largest index
          let maxIdx = -1;
          let largestFruit = null;
          fruitBodies.forEach(b => {
            const idx = parseInt(b.label.split('_')[1]);
            if (idx > maxIdx) {
              maxIdx = idx;
              largestFruit = b;
            }
          });
          if (largestFruit) {
            Matter.Composite.remove(this.engine.world, largestFruit);
          }
        }

        // Reset spawnTime for all remaining fruits to give a grace period
        const remainingFruits = Matter.Composite.allBodies(this.engine.world).filter(b => b.label?.startsWith('fruit_'));
        const now = Date.now();
        remainingFruits.forEach(b => {
          b.spawnTime = now;
        });
        this.revives = this.revives - 1 >= 0 ? this.revives - 1 : 0;
        this.gameOver = false;

        //this.startGame();
      };

      let advData = getRandomElement(this.advList);
      if (advData) {
        gameAdv.show(advData, this.timePeriod, res => {
          callback(res);
        }, this);
      } else {
        callback(null);
      }
    },
    countDown() {
      clearTimeout(this.stimeVal);
      this.leftTime = this.leftTime - 1;
      this.$nextTick(async () => {
        if (this.leftTime <= 0) {
          await this.init();
          this.popStartShow = true;
        } else {
          this.stimeVal = setTimeout(() => {
            this.countDown();
          }, 1000);
        }
      });
    },
    viewRank() {
      this.$nextTick(() => {
        //  this.$router.push(`/game/rank`)
        //  console.log(this.$router)
        this.end();
        this.popGameRankShow = true;
      });
    },
    async handleGetRewardConfirm() {
      let rs = await this.$http.post("/game/get/reward", {
        game_type: "circleMerge"
      });
      vailcode(rs, () => {
        this.popUserRewardShow = false;
      });
    }
  },
  async mounted() {
    if (!isOpenMobile()) {
      this.popOpenMobileShow = true;
      return;
    }
    await this.init();
    const gameCenterBox = this.$refs.gameCenterBox;
    this.GAME_WIDTH = gameCenterBox.clientWidth;
    this.GAME_HEIGHT = gameCenterBox.clientHeight;
    this.guideX = this.GAME_WIDTH / 2;
    this.popStartShow = true;
  }
};